import {
  type FC,
  type MouseEvent,
  type PropsWithChildren,
  useMemo,
} from 'react';
import type { GameV1 } from '@/api/games';
import { getGameCover } from '@/helpers/cdn';
import { useCMSGameCovers } from '@/modules/common/hooks/useCMSGameCovers';
import { useGamesProviders } from '@/modules/common/hooks/useGamesProviders';
import type { TCardMode } from '@/modules/game/types';
import {
  ScGameCardContainer,
  ScGameCardProvider,
  ScGameCardTitle,
} from './styled';

interface IGameCardWrapperProps extends PropsWithChildren {
  game: GameV1;
  mode: TCardMode;
  withoutBottom?: boolean;
  onPlayClick?: (event: MouseEvent) => void;
}

export const GameCardWrapper: FC<IGameCardWrapperProps> = ({
  children,
  game,
  mode,
  withoutBottom,
  onPlayClick,
}) => {
  const { id, title, provider } = game;
  const { covers } = useCMSGameCovers();

  const { data: providers } = useGamesProviders();

  const url = useMemo(
    () => covers?.[id]?.cover?.url || getGameCover(id),
    [covers, id]
  );

  const providerTitle = useMemo(() => {
    if (!providers.length) return provider;
    return providers.find(({ id }) => id === provider)?.title || provider;
  }, [providers, provider]);

  return (
    <ScGameCardContainer data-testid="game-card" mode={mode} url={url}>
      {children}
      {!withoutBottom && (
        <div className="bottom">
          <ScGameCardTitle
            data-testid="game-title"
            href={`/game/${game.fullPath}`}
            onClick={onPlayClick}
          >
            {title}
          </ScGameCardTitle>
          <ScGameCardProvider
            data-testid="game-provider"
            href={`/games/all/${provider}#gamesSection`}
          >
            {providerTitle}
          </ScGameCardProvider>
        </div>
      )}
    </ScGameCardContainer>
  );
};
